import { Box, Button, Stack, Theme, Typography, useTheme } from "@mui/material";
import { convertDate, convertTime } from "@/global/utils/dateTimeConvert";
import { CgCheck } from "react-icons/cg";
import { Divider } from "@/shared/base/components/Divider/Divider";
import { AnimatedCard } from "@/shared/base/components/AnimatedCard/AnimatedCard";
import { hexToRgba } from "@/global/theme/utils.theme";
import { MarqueeText } from "@/shared/common/layouts/MarqueeText/MarqueeText";
import {
  EventCardNoInventoryMessage,
  EventCardTag,
  EventCardWarningMessage,
} from "./event_card_helpers";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import { EventCardProps } from "./CombinedEventCard";
import { useEventCardCommon } from "./useEventCardCommon";
import NextImage from "next/image";
import { blurDataURL } from "@/global/common/blur-data-url";

export function FeaturedEventCard(props: EventCardProps) {
  const teamConfig = useTeamConfig();
  const theme = useTheme();
  const styles = useStyles(theme, props);

  const {
    isDisabled,
    isWithinSeatLimit,
    eventDate,
    eventTime,
    isPreviewButtonEnabled,
    isSoldOut,
    soldOutLabelDetails,
    isMarqueeDisabled,
  } = useEventCardCommon(props);
  const hideOverlayLogo = teamConfig?.uiConfigs?.featuredGameCard?.hideOverlayLogo; // prettier-ignore
  const showLogoWithTitle = teamConfig?.uiConfigs?.featuredGameCard?.showLogoWithTitle; // prettier-ignore
  const hideDateAndTime = teamConfig?.uiConfigs?.featuredGameCard?.hideDateAndTime || false; // prettier-ignore

  return (
    <Box position="relative">
      <Box
        sx={{
          pr: 2,
          top: 0,
          right: 0,
          zIndex: 2,
          position: "absolute",
          transform: "translate(0%, -50%)",
        }}
      >
        {isSoldOut ? (
          <EventCardTag
            label={soldOutLabelDetails?.label}
            color={soldOutLabelDetails?.color || theme.palette.primary.main}
            isMarqueeDisabled={isMarqueeDisabled}
          />
        ) : null}
      </Box>
      <AnimatedCard
        variant={props?.cardVariant}
        borderRadius={"10px"}
        footerHeight={"70px"}
        isSelected={props.isSelected}
        isDisabled={isDisabled}
        onClick={props?.onSelect}
        style={{ wrapper: { ...props?.slot?.wrapper?.style } }}
        renderBody={() => {
          return (
            <>
              <FeaturedGameCardImage
                logoUrl={props?.event?.logoUrl}
                backgroundUrl={props?.event?.backgroundUrl as string}
                overlayHex={props?.event?.overlayHex as string}
                overlayTransparency={
                  props?.event?.overlayTransparency as number
                }
                borderRadius="10px 10px 0 0"
                hideOverlayLogo={hideOverlayLogo}
              />
              <Box p={2} py={showLogoWithTitle ? 1.5 : 2}>
                <Stack
                  direction={"row"}
                  justifyContent={"flex-start"}
                  alignItems={"center"}
                  spacing={2}
                >
                  {props?.event?.logoUrl && showLogoWithTitle ? (
                    <Box width={"42px"} height={"42px"}>
                      <NextImage
                        src={props?.event?.logoUrl}
                        alt={"StadiumBackground team logo"}
                        width={42}
                        height={42}
                        style={{ objectFit: "contain", borderRadius: "4px" }}
                        loading="lazy"
                        placeholder="blur"
                        blurDataURL={blurDataURL}
                      />
                    </Box>
                  ) : null}

                  <Box textAlign={"left"}>
                    <Typography fontSize={"1rem"} fontWeight={700}>
                      {props?.event?.title}
                    </Typography>

                    {props?.event?.description ? (
                      <Typography fontSize={"0.9rem"}>
                        {props?.event?.description}
                      </Typography>
                    ) : null}
                  </Box>
                </Stack>
              </Box>
              {!hideDateAndTime ? (
                <>
                  <Divider />
                  <Box
                    display={"flex"}
                    alignItems={"center"}
                    padding={2}
                    gap={"0.5rem"}
                  >
                    <Typography
                      variant="body1"
                      noWrap
                      color="text.secondary"
                      fontSize={"0.9rem"}
                      fontWeight={"500"}
                    >
                      {convertDate(eventDate)}
                    </Typography>
                    <Divider orientation="vertical" variant="middle" />
                    <Typography
                      variant="body1"
                      noWrap
                      color={"text.secondary"}
                      fontSize={"0.9rem"}
                      fontWeight={"500"}
                    >
                      {convertTime(eventTime)}
                    </Typography>
                  </Box>
                </>
              ) : null}

              {!isWithinSeatLimit ? (
                <Box p={2} pt={0}>
                  <EventCardWarningMessage
                    description={
                      props.event.maxSeatLimit == 0
                        ? "This offer is no longer available."
                        : "You have purchased your maximum ticket limit for this event."
                    }
                  />
                </Box>
              ) : null}

              {props?.event?.hasInventory !== undefined &&
              !props?.event?.hasInventory &&
              isWithinSeatLimit ? (
                <Box p={2} pt={0}>
                  <EventCardNoInventoryMessage />
                </Box>
              ) : null}

              <Stack
                sx={styles.iconTagWrapper}
                direction={"row"}
                alignItems={"center"}
                justifyContent={"space-between"}
                width="100%"
                spacing={1}
              >
                <Box pl={2}>
                  <Box sx={styles.checkIcon}>
                    <CgCheck />
                  </Box>
                </Box>
                <Box flexGrow={1} />

                <Box>
                  {!isSoldOut && props?.event?.label ? (
                    <EventCardTag
                      label={props?.event?.label}
                      color={
                        props?.event?.labelHex || theme.palette.primary.main
                      }
                      isMarqueeDisabled={isMarqueeDisabled}
                      width="100px"
                    />
                  ) : null}
                </Box>

                <Box pr={2}>
                  {!isSoldOut && props?.event?.tag ? (
                    <EventCardTag
                      label={props?.event?.tag}
                      color={props?.event?.tagHex || theme.palette.primary.main}
                      isMarqueeDisabled={isMarqueeDisabled}
                      width="100px"
                    />
                  ) : null}
                </Box>
              </Stack>
            </>
          );
        }}
        renderFooter={() => {
          return (
            <Box width="100%" p={2}>
              <Stack spacing={2} direction={"row"}>
                {isPreviewButtonEnabled ? (
                  <Button
                    variant={"outlined"}
                    sx={{ width: "100%" }}
                    onClick={(ev) => {
                      ev.stopPropagation();
                      props?.onClickPreview?.();
                    }}
                  >
                    Preview
                  </Button>
                ) : null}

                <Button
                  variant={props?.isSelected ? "outlined" : "contained"}
                  sx={{ width: "100%" }}
                  onSelect={props?.onSelect}
                >
                  {props?.isSelected ? "Unselect" : "Select +"}
                </Button>
              </Stack>
            </Box>
          );
        }}
      />
    </Box>
  );
}

interface FeaturedGameCardImageProps {
  backgroundUrl?: string;
  logoUrl?: string;
  overlayHex?: string;
  overlayTransparency?: number;
  borderRadius?: string;
  hideOverlayLogo?: boolean;
}
function FeaturedGameCardImage(props: FeaturedGameCardImageProps) {
  const { uiConfigs } = useTeamConfig();
  const { overlayHex, overlayTransparency } = props;

  return (
    <Box position={"relative"} overflow={"hidden"} display={"flex"}>
      <NextImage
        src={props?.backgroundUrl || ""}
        alt={"Background image"}
        width={350}
        height={180}
        style={{
          objectFit: "cover",
          width: "100%",
          height: "100%",
          borderRadius: props?.borderRadius ? props.borderRadius : "inherit",
        }}
        loading="lazy"
        placeholder="blur"
        blurDataURL={blurDataURL}
      />

      {overlayHex ? (
        <Box
          borderRadius={props?.borderRadius ? props.borderRadius : "inherit"}
          position={"absolute"}
          width={"100%"}
          height="100%"
          bgcolor={hexToRgba(overlayHex, overlayTransparency || 0)}
          top="0"
          left="0"
        />
      ) : null}

      {props?.logoUrl && !props.hideOverlayLogo ? (
        <Box
          sx={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 3,
          }}
        >
          <NextImage
            src={props?.logoUrl}
            alt={"Team logo"}
            width={uiConfigs?.featuredGameCard?.overlayLogoWidth || 128}
            height={128}
            style={{ height: "auto", borderRadius: "4px" }}
            loading="lazy"
            placeholder="blur"
            blurDataURL={blurDataURL}
          />
        </Box>
      ) : null}
    </Box>
  );
}

function useStyles(theme: Theme, states: Partial<EventCardProps>) {
  const primaryColor = theme.palette.primary.main;
  const paperBackground = theme.palette.background.paper;
  const cardCubicBezire = "cubic-bezier(.27,.29,0,1)";

  return {
    iconTagWrapper: {
      position: "absolute",
      top: 0,
      left: 0,
      zIndex: 1,
      transform: "translate(0%, -50%) scale(1)",
      overflow: "hidden",
      pr: 1,
    },

    checkIcon: {
      fontSize: "24px",
      color: primaryColor,
      width: "24px",
      height: "24px",
      backgroundColor: paperBackground,
      borderRadius: "50%",
      border: `2px solid ${primaryColor}`,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      opacity: states.isSelected ? 1 : 0,
      transition: `all 0.2s ${cardCubicBezire}`,
      transform: `translateX(${states.isSelected ? 0 : "-10%"})`,
    },
  };
}
